import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Icon } from "@iconify-icon/react";
import { motion, AnimatePresence } from "motion/react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { useWorkspaceSettingsStore } from "../../store/useWorkspaceSettingsStore";
import { blockStudent, unblockStudent } from "../../api/workspaceSettings";

import { BlockStudentDialog } from "./BlockStudentDialog";

import { toast } from "@/utils/customToast";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import useUserStore from "@/features/auth/store/useUserStore";

interface MembersDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

interface BlockStudentFormData {
  reason: string;
}

const createBlockStudentSchema = (t: any) =>
  z.object({
    reason: z.string().max(255, t("workspaces:settings.members.dialog.reasonTooLong")).optional(),
  });

export const MembersDialog = ({ isOpen, onClose }: MembersDialogProps) => {
  const { t } = useTranslation(["workspaces", "common"]);
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { settings, setSettings } = useWorkspaceSettingsStore();
  const { user } = useUserStore();
  const [currentStep, setCurrentStep] = useState<"members" | "block">("members");
  const [selectedStudent, setSelectedStudent] = useState<{
    id: string;
    name: string;
    workspaceUserId: string;
  } | null>(null);
  const [processingUserId, setProcessingUserId] = useState<string | null>(null);

  // Block form states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reasonLength, setReasonLength] = useState(0);
  const [hasFutureLessonsError, setHasFutureLessonsError] = useState(false);
  const blockStudentSchema = createBlockStudentSchema(t);

  const form = useForm<BlockStudentFormData>({
    resolver: zodResolver(blockStudentSchema),
    defaultValues: {
      reason: "",
    },
  });

  if (!settings || !user) return null;

  const { members, user_status } = settings;
  const isTutor = user.role === "tutor";

  // Check if tutor can manage students (must be active and not blocked/left)
  const canManageStudents =
    isTutor &&
    user_status.is_active_for_user &&
    !user_status.is_blocked_for_user &&
    !user_status.has_user_left;

  const activeMembers = members.filter((member) => !member.left_at && !member.blocked_at);
  const blockedMembers = members.filter((member) => member.blocked_at);
  const leftMembers = members.filter((member) => member.left_at);

  const handleBlockStudent = (member: any) => {
    setSelectedStudent({
      id: member.id,
      name: member.full_name,
      workspaceUserId: member.workspace_user_id,
    });
    setCurrentStep("block");
    form.reset();
    setReasonLength(0);
    setHasFutureLessonsError(false);
  };

  const handleUnblockStudent = async (member: any) => {
    if (!workspaceId || !member.workspace_user_id) return;

    setProcessingUserId(member.id);
    try {
      await unblockStudent(workspaceId, member.workspace_user_id, null);

      // Update local state
      setSettings({
        ...settings,
        members: settings.members.map((m) =>
          m.id === member.id
            ? {
                ...m,
                blocked_at: null,
                blocked_reason: null,
                blocked_by: null,
              }
            : m,
        ),
      });

      toast.success(
        t("workspaces:settings.members.studentUnblocked", "Student unblocked successfully"),
      );
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        t("workspaces:settings.members.unblockFailed", "Failed to unblock student");

      toast.error(errorMessage);
    } finally {
      setProcessingUserId(null);
    }
  };

  const getErrorMessage = (errorCode: string, errorMessage?: string) => {
    const errorMap: Record<string, string> = {
      student_has_future_lessons: t(
        "workspaces:settings.members.errors.futureLessons",
        "You cannot block this student because they have reserved future lessons. Please cancel all their scheduled lessons first.",
      ),
      not_workspace_member: t(
        "workspaces:settings.members.errors.notWorkspaceMember",
        "You are not a member of this workspace.",
      ),
      user_not_student_in_workspace: t(
        "workspaces:settings.members.errors.notStudent",
        "The user is not a student in this workspace.",
      ),
      not_authenticated: t(
        "workspaces:settings.members.errors.notAuthenticated",
        "Authentication required",
      ),
      insufficient_permissions: t(
        "workspaces:settings.members.errors.insufficientPermissions",
        "Insufficient permissions",
      ),
    };

    return (
      errorMap[errorCode] ||
      errorMessage ||
      t(
        "workspaces:settings.members.errors.generic",
        "An error occurred while processing your request",
      )
    );
  };

  const handleBlockSubmit = async (data: BlockStudentFormData) => {
    if (!workspaceId || !selectedStudent?.workspaceUserId) return;

    setIsSubmitting(true);
    try {
      // Only include reason in request if it's not empty
      const reason = data.reason?.trim() || undefined;
      const requestData = reason ? { reason } : null;

      await blockStudent(workspaceId, selectedStudent.workspaceUserId, requestData);

      // Update local state
      setSettings({
        ...settings,
        members: settings.members.map((m) =>
          m.id === selectedStudent.id
            ? {
                ...m,
                blocked_at: new Date().toISOString(),
                blocked_reason: reason || null,
                blocked_by: user.id,
              }
            : m,
        ),
      });

      toast.success(
        t("workspaces:settings.members.studentBlocked", "Student blocked successfully"),
      );

      // Reset and go back to members list
      form.reset();
      setCurrentStep("members");
      setSelectedStudent(null);
    } catch (error: any) {
      console.error("Failed to block student:", error);

      const errorCode = error?.response?.data?.code;
      const errorMessage = error?.response?.data?.message;
      const errorMsg = getErrorMessage(errorCode, errorMessage);

      // Check if it's a future lessons error
      if (errorCode === "student_has_future_lessons") {
        setHasFutureLessonsError(true);
      }

      // Handle validation errors specifically
      if (error?.response?.status === 422 && error?.response?.data?.errors) {
        const validationErrors = error.response.data.errors;

        if (validationErrors.reason) {
          form.setError("reason", {
            type: "server",
            message: validationErrors.reason[0],
          });
        }
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      form.reset();
      setReasonLength(0);
      setHasFutureLessonsError(false);
      setCurrentStep("members");
      setSelectedStudent(null);
      onClose();
    }
  };

  const handleGoBackToMembers = () => {
    if (!isSubmitting) {
      form.reset();
      setReasonLength(0);
      setHasFutureLessonsError(false);
      setCurrentStep("members");
      setSelectedStudent(null);
    }
  };

  const renderMemberCard = (member: any, section: "active" | "blocked" | "left") => {
    const isStudent = member.type === "student";
    const canManage = canManageStudents && isStudent && member.id !== user.id;
    const isProcessing = processingUserId === member.id;

    return (
      <div className="group bg-muted/50 hover:bg-muted/70 relative cursor-pointer overflow-hidden rounded-lg p-3 backdrop-blur-sm transition-all duration-300 hover:scale-[1.02]">
        {/* Background blur effect like in NewInvitationDialog */}
        <div className="absolute inset-0 top-1/2 -left-0 h-24 w-24 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full opacity-25">
          <img
            alt={member.full_name}
            className="h-full w-full object-cover blur-2xl"
            src={member.avatar_path || member.avatar || "/placeholder.svg?height=40&width=40"}
          />
        </div>

        <div className="relative flex items-center gap-3">
          <Avatar className="h-10 w-10">
            <AvatarImage
              alt={member.full_name}
              src={member.avatar_path || member.avatar || "/placeholder.svg?height=40&width=40"}
            />
            <AvatarFallback>
              {member.full_name
                .split(" ")
                .map((n: string) => n[0])
                .join("")}
            </AvatarFallback>
          </Avatar>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h4 className="text-accent-foreground truncate text-sm font-medium">
                {member.full_name}
              </h4>
              <Badge
                className="text-xs"
                variant={member.type === "tutor" ? "default" : "secondary"}
              >
                {t(`common:roles.${member.type}`, member.type === "tutor" ? "Tutor" : "Student")}
              </Badge>
              {section === "blocked" && (
                <Badge className="text-xs" variant="destructive">
                  {t("workspaces:settings.members.blocked", "Blocked")}
                </Badge>
              )}
              {section === "left" && (
                <Badge className="text-xs" variant="outline">
                  {t("workspaces:settings.members.left", "Left")}
                </Badge>
              )}
            </div>
            {member.blocked_reason && section === "blocked" && (
              <p className="text-muted-foreground mt-1 truncate text-xs">{member.blocked_reason}</p>
            )}
          </div>

          {canManage && section === "active" && (
            <Button
              className="h-6 gap-1 px-2 text-xs"
              disabled={isProcessing}
              size="sm"
              variant="destructive"
              onClick={() => handleBlockStudent(member)}
            >
              {isProcessing ? (
                <Icon className="animate-spin" icon="lucide:loader-2" />
              ) : (
                <Icon icon="mdi:block-helper" />
              )}
              {t("workspaces:settings.members.blockStudent", "Block")}
            </Button>
          )}

          {canManage && section === "blocked" && (
            <Button
              className="h-6 gap-1 px-2 text-xs"
              disabled={isProcessing}
              size="sm"
              variant="foreground"
              onClick={() => handleUnblockStudent(member)}
            >
              {isProcessing ? (
                <Icon className="animate-spin" icon="lucide:loader-2" />
              ) : (
                <Icon icon="mdi:shield-check" />
              )}
              {t("workspaces:settings.members.unblockStudent", "Unblock")}
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderMembersStep = () => (
    <div className="space-y-6">
      {/* Active Members */}
      {activeMembers.length > 0 && (
        <div>
          <h3 className="text-accent-foreground mb-3 text-lg font-semibold">
            {t("workspaces:settings.members.activeMembers", "Active Members")} (
            {activeMembers.length})
          </h3>
          <div className="space-y-2">
            {activeMembers.map((member, index) => (
              <motion.div
                key={member.id}
                animate={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                {renderMemberCard(member, "active")}
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Blocked Members */}
      {blockedMembers.length > 0 && (
        <div>
          <h3 className="text-accent-foreground mb-3 text-lg font-semibold">
            {t("workspaces:settings.members.blockedMembers", "Blocked Members")} (
            {blockedMembers.length})
          </h3>
          <div className="space-y-2">
            {blockedMembers.map((member, index) => (
              <motion.div
                key={member.id}
                animate={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                {renderMemberCard(member, "blocked")}
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Left Members */}
      {leftMembers.length > 0 && (
        <div>
          <h3 className="text-accent-foreground mb-3 text-lg font-semibold">
            {t("workspaces:settings.members.leftMembers", "Left Members")} ({leftMembers.length})
          </h3>
          <div className="space-y-2">
            {leftMembers.map((member, index) => (
              <motion.div
                key={member.id}
                animate={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                {renderMemberCard(member, "left")}
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Close Button */}
      <div className="flex justify-end pt-4">
        <Button
          className="bg-muted/20 hover:bg-muted/30 border-border/50 rounded-xl"
          variant="outline"
          onClick={handleClose}
        >
          {t("common:close")}
        </Button>
      </div>
    </div>
  );

  const renderBlockStep = () => (
    <div className="space-y-6">
      <div>
        <p className="text-muted-foreground mb-4 text-sm">
          {t("workspaces:settings.members.dialog.description", {
            defaultValue:
              "Are you sure you want to block {{studentName}}? This will prevent them from accessing the workspace and all its resources.",
            studentName: selectedStudent?.name || "",
          })}
        </p>

        <Form {...form}>
          <form className="space-y-6" onSubmit={form.handleSubmit(handleBlockSubmit)}>
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <div>
                  <FormItem className="space-y-3">
                    <div className="flex items-center justify-between">
                      <FormLabel className="text-accent-foreground flex items-center gap-2 text-lg font-medium">
                        <Icon height={"1.25rem"} icon="mdi:text-box" width={"1.25rem"} />
                        {t("workspaces:settings.members.dialog.reasonLabel", "Reason for blocking")}
                      </FormLabel>
                      <span className="text-muted-foreground text-sm">
                        {reasonLength}/{255}
                      </span>
                    </div>
                    <FormControl>
                      <Card className="border-input overflow-hidden rounded-xl border shadow-sm">
                        <CardContent className="p-0">
                          <ScrollArea className="border-input bg-background ring-offset-background focus-within:ring-ring h-32 w-full rounded-xl">
                            <Textarea
                              className="[field-sizing:content] min-h-[128px] resize-none rounded-xl border-none! p-4 text-base break-words whitespace-pre-wrap focus-visible:ring-0 focus-visible:ring-offset-0"
                              placeholder={t(
                                "workspaces:settings.members.dialog.reasonPlaceholder",
                                "Enter reason for blocking this student (optional)",
                              )}
                              {...field}
                              maxLength={255}
                              onChange={(e) => {
                                field.onChange(e.target.value);
                                setReasonLength(e.target.value.length);
                              }}
                            />
                          </ScrollArea>
                        </CardContent>
                      </Card>
                    </FormControl>
                  </FormItem>
                </div>
              )}
            />

            {/* Action buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                className="bg-muted/20 hover:bg-muted/30 border-border/50 rounded-xl"
                disabled={isSubmitting}
                type="button"
                variant="outline"
                onClick={handleGoBackToMembers}
              >
                {t("common:back", "Back")}
              </Button>
              <Button
                className="gap-2 rounded-xl"
                disabled={isSubmitting || hasFutureLessonsError}
                type="submit"
                variant="destructive"
              >
                {isSubmitting ? (
                  <>
                    <Icon className="animate-spin" icon="lucide:loader-2" />
                    {t("workspaces:settings.members.dialog.blocking", "Blocking...")}
                  </>
                ) : (
                  <>
                    <Icon icon="mdi:block-helper" />
                    {t("workspaces:settings.members.dialog.blockButton", "Block Student")}
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="bg-foreground/80 h-fit max-h-dvh max-w-4xl border-none p-0 backdrop-blur-xl">
          <ScrollArea className="h-full max-h-dvh">
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="p-6"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
            >
              <DialogHeader className="pb-4">
                <motion.div
                  key={`header-${currentStep}`}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-accent-foreground flex items-center gap-3 text-xl font-semibold"
                  initial={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  {currentStep === "block" && (
                    <motion.div
                      animate={{ opacity: 1, x: 0 }}
                      initial={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2, delay: 0.1 }}
                    >
                      <Button
                        className="bg-muted/20 hover:bg-muted/30 border-border/50 mr-2 h-10 w-10 p-0"
                        variant="outline"
                        onClick={handleGoBackToMembers}
                      >
                        <Icon className="h-4 w-4" icon="lucide:arrow-left" />
                      </Button>
                    </motion.div>
                  )}
                  <motion.div
                    key={`icon-${currentStep}`}
                    animate={{ scale: 1, rotate: 0 }}
                    className="bg-muted/20 flex size-10 items-center justify-center rounded-xl backdrop-blur-sm"
                    initial={{ scale: 0.8, rotate: currentStep === "block" ? -10 : 10 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  >
                    <Icon
                      className={
                        currentStep === "block" ? "text-red-500" : "text-accent-foreground"
                      }
                      height="1.25rem"
                      icon={currentStep === "block" ? "mdi:block-helper" : "mdi:account-multiple"}
                      width="1.25rem"
                    />
                  </motion.div>
                  <motion.span
                    key={`title-${currentStep}`}
                    animate={{ opacity: 1, x: 0 }}
                    initial={{ opacity: 0, x: 10 }}
                    transition={{ duration: 0.2, delay: 0.1 }}
                  >
                    {currentStep === "members"
                      ? t("workspaces:settings.members.dialog.title", "Workspace Members")
                      : t("workspaces:settings.members.dialog.blockTitle", "Block Student")}
                  </motion.span>
                </motion.div>
              </DialogHeader>

              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: currentStep === "block" ? -20 : 20 }}
                  initial={{ opacity: 0, x: currentStep === "block" ? 20 : -20 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                  {currentStep === "members" ? renderMembersStep() : renderBlockStep()}
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <BlockStudentDialog
        isOpen={false}
        studentName={selectedStudent?.name || ""}
        onClose={() => {}}
        onConfirm={async () => {}}
      />
    </>
  );
};
